import { useCallback, useState } from 'react';
import { Inheritance, Module, ScopeEnum } from 'src/domain';
import { TableOverviewRow } from 'src/domain/table/table-overview-row';
import { useChangesSnackbar, useScope } from 'src/hooks';
import { useDiscardChangesModule } from 'src/hooks/discard-changes/useDiscardChangesModule';
import { useCompositePartner } from 'src/hooks/partner/useCompositePartner';
import { useUpdateSplitLevel } from 'src/hooks/split-levels/useUpdateSplitLevel';
import { useCurrentId } from 'src/hooks/useCurrentId';
import { useEntityType } from 'src/hooks/useEntityType';
import { components } from 'src/infrastructure/rest-api/generated';
import { useApiMutation } from 'src/infrastructure/rest-api/useApi';
import { useGenderSplitOverviewSimulationRows } from '../simulation/useGenderSplitOverviewSimulationRows';

function valueMapper(row: TableOverviewRow) {
    return Object.keys(row.columns).reduce<Array<components['schemas']['UpdateGenderSplitInput']>>((acc, key) => {
        if (key.endsWith('Id')) return acc;

        const id = row.columns[`${key}Id`];
        const value = row.columns[key];

        acc.push({
            id,
            splitPercent: value,
        });

        return acc;
    }, []);
}

export const useGenderSplitSaveChanges = () => {
    const currentId = useCurrentId();
    const scope = useScope();
    const entityType = useEntityType();
    const compositePartner = useCompositePartner();

    const [mutateGenderSplit] = useApiMutation('/api/gendersplit', 'put', {
        update(data, _variables, queryClient) {
            const query = { entityId: Number(currentId), entityType };
            if (scope === ScopeEnum.PARTNER) {
                query['entityId'] = Number(compositePartner?.id);
            }
            queryClient.setQueryData(['/api/gendersplit', query], data);
            queryClient.invalidateQueries({
                queryKey: ['/api/inheritance', query],
            });
        },
    });

    const { updateSplitLevel: updateStoreSplitLevel } = useUpdateSplitLevel();

    const showSnackbar = useChangesSnackbar();
    const discardChanges = useDiscardChangesModule(Module.GenderSplit);

    const { data: valuesToUpdate } = useGenderSplitOverviewSimulationRows();
    const [isUpdating, setIsUpdating] = useState(false);

    const sendUpdateQuery = useCallback(async () => {
        if (!valuesToUpdate) {
            return;
        }

        const payload = valuesToUpdate.flatMap(valueMapper);

        if (scope === ScopeEnum.STORE) {
            await updateStoreSplitLevel(Inheritance.Typed);
        }

        return mutateGenderSplit({
            body: payload,
        });
    }, [mutateGenderSplit, scope, updateStoreSplitLevel, valuesToUpdate]);

    const updateGenderSplit = useCallback(async () => {
        setIsUpdating(true);

        try {
            const response = await sendUpdateQuery();
            discardChanges();
            showSnackbar();
            return response;
        } catch (err) {
            showSnackbar(err);
        } finally {
            setIsUpdating(false);
        }
    }, [discardChanges, sendUpdateQuery, showSnackbar]);

    return [updateGenderSplit, { loading: isUpdating }] as const;
};
